import { EmptyMuted } from "@/components/ui/empty-muted";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import ResponsiveDialogDrawer from "@/features/shared/components/responsive-dialog-drawer/responsive-dialog-drawer";
import { StaggerFadeIn } from "@/features/shared/components/stagger-fade-in";
import { type UserTicket, useUserTicketsQuery } from "@/features/user/api/use-user-tickets-query";
import TicketList from "@/features/user/components/ticket-list/ticketlist";
import TicketListContent from "@/features/user/components/ticket-list/ticketlistcontent";
import SkeletonLoading from "@/features/user/components/tickets/skeleton-loading";
import { useLng } from "@/hooks/use-lng";
import { appTitleHead } from "@/lib/app-document-title";
import { useTranslation } from "@/lib/i18n/client";
import { useConfigStore } from "@/store/use-config-store";
import { Ticket } from "@/types/ticketsInterfaces";
import { useLocalStorage } from "@/utils/useLocalStorage";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { isAfter, isBefore } from "date-fns";
import { TicketsIcon } from "lucide-react";
import { useAuth } from "oidc-react";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";

export const Route = createFileRoute("/_app/account/_auth/tickets")({
  head: () => appTitleHead("account-tickets-title"),
  component: TicketsPage
});

function TicketsPage() {
  const { userData } = useAuth();
  const [settings, localSettings] = useConfigStore(
    useShallow((state) => [state.settings, state.localSettings])
  );
  const lng = useLng();
  const { t } = useTranslation(lng, "translation");
  const [active, setActive] = useState<null | Ticket>(null);
  const [isChecked, setChecked] = useLocalStorage("isCheckedTickets", true);

  const { data: ticketsData, isLoading: ticketsIsLoading } = useUserTicketsQuery();

  const sortByDate = (a: UserTicket, b: UserTicket) => {
    if (a.validFrom < b.validFrom) return 1;
    if (b.validFrom < a.validFrom) return -1;
    if (a.validUntil < b.validUntil) return 1;
    if (b.validUntil < a.validUntil) return -1;
    return 0;
  };

  const userTickets =
    ticketsData?.member?.tickets?.map((ticket) => ({
      ...ticket,
      ownerName: userData?.profile?.name ?? null
    })) ?? [];
  const managedTickets =
    ticketsData?.member?.manages?.flatMap((manage) =>
      manage.tickets.map((ticket) => ({
        ...ticket,
        ownerName: manage.name ?? null
      }))
    ) ?? [];
  const allTickets = [...userTickets, ...managedTickets];

  const normalizeTicket = (ticket: UserTicket): UserTicket =>
    ticket.validFrom === "0001-01-01T00:00:00"
      ? { ...ticket, validFrom: "", validUntil: "" }
      : ticket;

  const tickets = (
    isChecked
      ? allTickets.toSorted(sortByDate)
      : allTickets
          .filter(
            (ticket) =>
              isBefore(new Date(ticket.validFrom), new Date()) &&
              isAfter(new Date(ticket.validUntil), new Date())
          )
          .toSorted(sortByDate)
  ).map(normalizeTicket);

  const additionalTicketData = tickets.find((ticket) => ticket.id === active?.id);

  const hasAnyTickets =
    (ticketsData?.member?.tickets?.length ?? 0) +
      (ticketsData?.member?.manages?.reduce(
        (sum, managedUser) => sum + (managedUser.tickets?.length ?? 0),
        0
      ) ?? 0) >
    0;

  const hiddenTickets = !!tickets && tickets.length === 0 && hasAnyTickets;

  if (
    settings &&
    localSettings &&
    (!settings?.useMobileTicket || !localSettings?.showMobileTicket)
  ) {
    throw notFound();
  }

  return (
    <div className="flex w-full flex-col">
      <h1 className="hidden text-left md:block">{t("nav-tickets")}</h1>
      {ticketsIsLoading && <Skeleton className="h-8 w-full max-w-100 rounded-md" />}
      {tickets && tickets?.length > 0 ? (
        <p>{t("ticket-page-read-more")}</p>
      ) : hiddenTickets ? (
        <p>{t("ticket-page-no-active-tickets")}</p>
      ) : null}

      <div className="mt-8">
        {!ticketsIsLoading && tickets?.length === 0 && (
          <EmptyMuted title={t("ticket-page-no-tickets")} description="" icon={<TicketsIcon />} />
        )}
        {ticketsIsLoading && <SkeletonLoading />}
        {((tickets && tickets.length > 0) || hiddenTickets) && (
          <div className="overflow-hidden">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="p-0">{t("ticket-page-header")}</h2>
              <span className="flex items-center justify-between gap-3 p-0 text-sm font-semibold">
                {t("show-inactive")}
                <Switch
                  checked={isChecked}
                  onCheckedChange={(newChecked) => setChecked(newChecked)}
                  aria-label={isChecked ? t("show-inactive-aria-on") : t("show-inactive-aria-off")}
                />
              </span>
            </div>
            {tickets && tickets.length > 0 && (
              <StaggerFadeIn key={String(isChecked)} className="flex flex-col">
                {tickets.map((ticket: UserTicket) => (
                  <TicketList
                    key={ticket.id}
                    data={ticket}
                    active={active}
                    setActive={setActive}
                    ownerName={ticket.ownerName}
                  />
                ))}
              </StaggerFadeIn>
            )}
          </div>
        )}
        <ResponsiveDialogDrawer open={!!active} close={() => setActive(null)}>
          {active && additionalTicketData && (
            <TicketListContent data={active} additionalTicketData={additionalTicketData} />
          )}
        </ResponsiveDialogDrawer>
      </div>
    </div>
  );
}
